import { useOfficeCapabilities } from "src/api/federation-office";
import { AUDIT_REPORT_PATH, useAuditReportOptions } from "src/api/federation-audit-report";
import { useFederationExportOptions } from "src/api/federation-export";
import { useIntegrationCapabilities } from "src/api/federation-integration";
import { useCommunicationCapabilities } from "src/api/federation-communication";
import {useMovementCapabilities,useMovementMemberships} from "src/api/federation-transfer";
import { useDocumentCapabilities } from 'src/api/federation-document';
import { useFinanceCapabilities, useFinanceObligations } from "src/api/federation-finance";
import { useLicenceCapabilities, useLicenceMemberships } from "src/api/federation-licence";
import { useMedicalCapabilities, useMedicalMemberships } from "src/api/federation-medical";
import { useAssociationCapabilities } from "src/api/association";
import { useMemo } from "react";
import { useGetFederationAssignments } from "src/api/federation-assignment";
import { useFederationApprovals } from "src/api/federation-decision";
import { useFederationOrganisations } from "src/api/federation-organisation";
import { useGetFederationRulesets } from "src/api/federation-ruleset";
import { FEDERATION_CAPABILITIES } from "src/types/federation";

import { useTranslate } from "src/locales";

import { useAuthContext } from "src/auth/hooks";
import type { NavItemBaseProps, NavProps } from "src/components/nav-section";
import { useFederationContext } from "src/context/FederationContext";
import type { FederationCapability } from "src/types/federation";
import { getOrganiserNav } from "./admintypenavs/organiserNav";
import { getFacilityNav } from "./admintypenavs/facilityNav";
import { paths } from "src/routes/paths";
import { ICONS, diagnosticsIcon } from "./admintypenavs/menuIcons";
import { groupFederationNav } from "./admintypenavs/federationNav";

// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

function filterNavItems(
  items: NavItemBaseProps[],
  hasCapability: (capability: FederationCapability) => boolean,
): NavItemBaseProps[] {
  return items.flatMap((item) => {
    if (
      item.requiredFederationCapability &&
      !hasCapability(item.requiredFederationCapability)
    ) {
      return [];
    }

    return [
      item.children
        ? { ...item, children: filterNavItems(item.children, hasCapability) }
        : item,
    ];
  });
}

export function filterNavDataByFederationCapability(
  data: NavProps["data"],
  hasCapability: (capability: FederationCapability) => boolean,
): NavProps["data"] {
  return data
    .map((group) => ({
      ...group,
      items: filterNavItems(group.items, hasCapability),
    }))
    .filter((group) => group.items.length > 0);
}

// FS-2026-068: append after every provider-specific and federation transformation.
// This is a destination only; its endpoint still authorizes each category.
export function appendDiagnosticsNav(data: NavProps["data"], title: string): NavProps["data"] {
  return [...data, { subheader: "", items: [{ title, path: paths.dashboard.diagnostics, icon: diagnosticsIcon }] }];
}

export function useNavData() {
  const { t } = useTranslate();
  const { currentProviderType, currentProvider } = useAuthContext();
  const { hasCapability, context, loading, error } = useFederationContext();
  const association = useAssociationCapabilities(
    Number(currentProvider) || 0,
    currentProviderType === "association",
  );
  const finance = useFinanceCapabilities(Number(currentProvider) || 0, hasCapability(FEDERATION_CAPABILITIES.finance));
  const integrations = useIntegrationCapabilities(Number(currentProvider) || 0, hasCapability(FEDERATION_CAPABILITIES.integrations));
  const office = useOfficeCapabilities(Number(currentProvider) || 0, hasCapability("federation.office"));
  const auditReports = useAuditReportOptions(Number(currentProvider) || 0,
    context.context_type === "federation" && context.workspace_provider_id === Number(currentProvider) && hasCapability("federation.audit"));
  const communications = useCommunicationCapabilities(Number(currentProvider) || 0, hasCapability(FEDERATION_CAPABILITIES.communications) || hasCapability(FEDERATION_CAPABILITIES.legal_acceptance));
  const licences = useLicenceCapabilities(Number(currentProvider) || 0, hasCapability(FEDERATION_CAPABILITIES.licences));
  const clubLicences = useLicenceMemberships(Number(currentProvider) || 0, currentProviderType === "association");
  const documents = useDocumentCapabilities(Number(currentProvider) || 0, hasCapability(FEDERATION_CAPABILITIES.documents));
  const movements=useMovementCapabilities(Number(currentProvider)||0,hasCapability(FEDERATION_CAPABILITIES.transfers));
  const movementMembers=useMovementMemberships(Number(currentProvider)||0,currentProviderType==="association");
  const medical = useMedicalCapabilities(Number(currentProvider) || 0, hasCapability(FEDERATION_CAPABILITIES.medical));
  const associationMedical = useMedicalMemberships(Number(currentProvider) || 0, currentProviderType === "association");
  const associationFinance = useFinanceObligations("association", Number(currentProvider) || 0, currentProviderType === "association");
  const playerAccess = usePlayerCapabilities(Number(currentProvider) || 0, currentProviderType === "association" || hasCapability(FEDERATION_CAPABILITIES.players), currentProviderType === "association");
  const canReadFinance = Boolean(finance.data?.allowed_actions.includes("federation_finance_read")) && !finance.error;
  const isFederation = context.context_type === "federation";
  const hasFederationPortal = !loading && !error && isFederation &&
    currentProviderType === "organiser" &&
    context.workspace_provider_id === Number(currentProvider) &&
    context.federation_provider_id === Number(currentProvider);
  const organisationAccessEnabled =
    !loading &&
    context.workspace_provider_id === Number(currentProvider) &&
    (isFederation
      ? hasCapability(FEDERATION_CAPABILITIES.organisations)
      : currentProviderType === "association");
  const organisations = useFederationOrganisations(
    Number(currentProvider) || 0,
    isFederation,
    organisationAccessEnabled,
  );
  const canReadOrganisations =
    organisationAccessEnabled &&
    Boolean(organisations.data) &&
    !organisations.error &&
    !organisations.isLoading;
  const rulesets = useGetFederationRulesets(
    Number(currentProvider) || 0,
    hasCapability(FEDERATION_CAPABILITIES.rulesets),
  );
  const assignments = useGetFederationAssignments(
    Number(currentProvider) || 0,
    hasCapability(FEDERATION_CAPABILITIES.assignments),
  );
  const canReadAssignments =
    Boolean(assignments.data) && !assignments.error && !assignments.isLoading;
  const canReadRulesets =
    Boolean(rulesets.data) && !rulesets.error && !rulesets.isLoading;
  const approvals = useFederationApprovals(
    Number(currentProvider) || 0,
    hasCapability(FEDERATION_CAPABILITIES.approvals),
  );
  const canReadApprovals =
    Boolean(approvals.data) && !approvals.error && !approvals.isLoading;
  const exportOptions = useFederationExportOptions(
    Number(currentProvider) || 0,
    isFederation && hasCapability(FEDERATION_CAPABILITIES.audit),
  );
  const canExport = Boolean(exportOptions.data?.datasets.length) && !exportOptions.error;

  const data = useMemo(
    () => {
      if (currentProviderType === "association") {
        const granted = association.data?.permissions ?? [];
        return [
          {
            subheader: t("association_dashboard"),
            items: [
              {
                title: t("association_dashboard"),
                path: "/dashboard/association",
                icon: ICONS.dashboard,
              },
              ...(granted.includes("association_members_read")
                ? [
                    {
                      title: t("association_members"),
                      path: "/dashboard/association/members",
                      icon: ICONS.user,
                    },
                  ]
                : []),
              ...(associationFinance.data && !associationFinance.error ? [{ title: t("finance_title"), path: "/dashboard/association/federation-finance", icon: ICONS.user }] : []),
              ...(granted.includes("association_fees_read")
                ? [
                    {
                      title: t("association_fees"),
                      path: "/dashboard/association/fees",
                      icon: ICONS.invoice,
                    },
                  ]
                : []),
              ...(canReadOrganisations
                ? [
                    {
                      title: t("federation_org_memberships"),
                      path: "/dashboard/association/federations",
                      icon: ICONS.user,
                    },
                  ]
                : []),
              {
                title: t("profile"),
                path: "/dashboard/association/profile",
                icon: ICONS.file,
              },
              {
                title: t("settings"),
                path: "/dashboard/association/settings",
                icon: ICONS.product,
              },
            ],
          },
        ];
      }
      if (
        currentProviderType === "undefined" ||
        currentProviderType === undefined ||
        currentProviderType === "facility" ||
        currentProviderType === "coach"
      ) {
        return filterNavDataByFederationCapability(
          getFacilityNav(),
          hasCapability,
        );
      } else if (currentProviderType === "organiser") {
        // NAVIGATION FOR TOURNAMENT PROVIDER
        return filterNavDataByFederationCapability(
          getOrganiserNav(
            currentProviderType,
            canReadRulesets,
            canReadAssignments,
            canReadApprovals,
            canReadFinance,
            hasFederationPortal,
            canExport,
          ),
          hasCapability,
        ); // 👈 uses the extracted file
      }

      return [];
    },
    //[venueMenu, t]
    [
      t,
      currentProviderType,
      association.data,
      associationFinance.data,
      associationFinance.error,
      canReadFinance,
      hasCapability,
      canReadRulesets,
      canReadAssignments,
      canReadApprovals,
      canExport,
      canReadOrganisations,
      hasFederationPortal,
    ],
  );

  const baseResult = canReadOrganisations && isFederation && currentProviderType !== "association"
    ? [
        ...data,
        {
          subheader: "",
          items: [
            {
              title: t("federation_org_title"),
              path: paths.dashboard.organiser.federation_organisations,
              icon: ICONS.user,
            },
          ],
        },
      ]
    : data;
  const withIntegrations = !integrations.error && integrations.data?.allowed_actions.includes("federation_integrations_read")
    ? [...baseResult, { subheader: "", items: [{ title: t("fint_title"), path: "/dashboard/organiser/federation-integrations", icon: ICONS.user }] }] : baseResult;
  const withOffice = !office.error && office.data?.categories.length
    ? [...withIntegrations, { subheader: "", items: [{ title: t("office_title"), path: "/dashboard/organiser/federation-office", icon: ICONS.user }] }] : withIntegrations;
  const withReports = !auditReports.error && auditReports.data?.families.length
    ? [...withOffice, { subheader: "", items: [{ title: t("ar_title"), path: AUDIT_REPORT_PATH, icon: ICONS.user }] }] : withOffice;
  const result = !communications.error && communications.data?.allowed_actions.length
    ? [...withReports, { subheader: "", items: [{ title: t("comm_title"), path: "/dashboard/organiser/federation-communications", icon: ICONS.user }] }]
    : withReports;
  const playerActions = playerAccess.error ? [] : playerAccess.data?.allowed_actions || [];
  const withDiagnostics = (navigation: NavProps["data"]) => appendDiagnosticsNav(navigation, t("diagnostics_title"));
  if (currentProviderType === "association") {
    const associationItems = [
      { title: t("teams"), path: "/dashboard/association/teams", icon: ICONS.user },
      ...(!movementMembers.error&&movementMembers.data?.items.length?[{title:t("ft_title"),path:"/dashboard/association/federation-transfers",icon:ICONS.user}]:[]),
      ...(!clubLicences.error && clubLicences.data?.items.length ? [{ title: t("fl_title"), path: "/dashboard/association/federation-licences", icon: ICONS.user }] : []),
      ...(!associationMedical.error && associationMedical.data?.items.length ? [{ title: t("med_title"), path: "/dashboard/association/federation-medical-eligibility", icon: ICONS.user }] : []),
    ];
    const clubResult = associationItems.length ? [...result, { subheader: "", items: associationItems }] : result;
    if (!organisations.data?.items.some(item => item.connection_enabled)) return withDiagnostics(clubResult);
    if (playerActions.some(key => ["federation_players_read", "federation_registrations_review"].includes(key)))
      return withDiagnostics([...clubResult, { subheader: "", items: [{ title: t("fr_players"), path: "/dashboard/association/player-registrations", icon: ICONS.user }] }]);
    return withDiagnostics(clubResult);
  }
  const licenceItems = !licences.error && licences.data?.allowed_actions.includes("federation_licences_read")
    ? [{ title: t("fl_title"), path: "/dashboard/organiser/federation-licences", icon: ICONS.user }] : [];
  const medicalItems = !medical.error && medical.data?.allowed_actions.some(key => ["federation_medical_read", "federation_eligibility_settings_manage"].includes(key))
    ? [{ title: t("med_title"), path: "/dashboard/organiser/federation-medical-eligibility", icon: ICONS.user }] : [];
  const movementItems=!movements.error&&movements.data?.allowed_actions.length?[{title:t("ft_title"),path:"/dashboard/organiser/federation-transfers",icon:ICONS.user}]:[];
  const documentItems = !documents.error && documents.data?.allowed_actions.length
    ? [{ title: t("fd_title"), path: "/dashboard/organiser/federation-documents", icon: ICONS.user }] : [];
  const items = [
    ...licenceItems,
    ...medicalItems,
    ...documentItems,
    ...movementItems,
    ...(hasCapability(FEDERATION_CAPABILITIES.players) && playerActions.some(key => ["federation_players_read", "federation_registrations_review", "federation_players_manage"].includes(key)) ? [{ title: t("fr_players"), path: "/dashboard/organiser/federation-players", icon: ICONS.user }] : []),
    ...(hasCapability(FEDERATION_CAPABILITIES.players) && playerActions.includes("federation_registration_settings_manage") ? [{ title: t("fr_settings"), path: "/dashboard/organiser/federation-player-registration-settings", icon: ICONS.user }] : []),
  ];
  const navigation = items.length ? [...result, { subheader: "", items }] : result;
  return withDiagnostics(isFederation && !loading && context.workspace_provider_id === Number(currentProvider) && currentProviderType === "organiser"
    ? groupFederationNav(navigation, t)
    : navigation);
}

/*-------------------------------------------------*/
// const { globalsettings, globalsettingsLoading, globalsettingsError } =
//   useGetProviderSettingsGlobals(currentProvider);

// const [venueBased, setVenueBased] = useState();

// useEffect(() => {
//   if (globalsettings) {
//     setVenueBased(
//       globalsettings.find(
//         (setting: any) => setting.name === "calendarviewtype"
//       )?.value
//     );
//   }
// }, [globalsettings]);

// const venueMenu =
//   venueBased === "venue-based"
//     ? {
//         title: t("venue"),
//         path: paths.dashboard.settings.venue.root,
//       }
//     : null;
// {
//   subheader: t("profilesettings"),
//   items: [
//     {
//       title: t("profile_data"),
//       path: paths.dashboard.profile.root,
//       icon: ICONS.file,
//       children: [
//         {
//           title: t("profile"),
//           path: paths.dashboard.profile.data,
//         },
//         { title: t("images"), path: paths.dashboard.profile.images },
//         { title: t("sports"), path: paths.dashboard.profile.sports },
//         { title: t("venues"), path: paths.dashboard.profile.venues },

//         {
//           title: t("billing_infos"),
//           path: paths.dashboard.profile.billing_infos,
//         },
//         // { title: t("staff"), path: paths.dashboard.profile.staff },
//       ],
//     },
//   ],
// },

// {
//   subheader: t("test"),
//   items: [
//     // USER
//     {
//       title: t("user"),
//       path: paths.dashboard.user.root,
//       icon: ICONS.user,
//       children: [
//         { title: t("profile"), path: paths.dashboard.user.root },
//         { title: t("cards"), path: paths.dashboard.user.cards },
//         { title: t("list"), path: paths.dashboard.user.list },
//         { title: t("create"), path: paths.dashboard.user.new },
//         { title: t("edit"), path: paths.dashboard.user.demo.edit },
//         { title: t("account"), path: paths.dashboard.user.account },
//       ],
//     },
//     {
//       title: t("product"),
//       path: paths.dashboard.product.root,
//       icon: ICONS.product,
//       children: [
//         { title: t("list"), path: paths.dashboard.product.root },
//         {
//           title: t("details"),
//           path: paths.dashboard.product.demo.details,
//         },
//         { title: t("create"), path: paths.dashboard.product.new },
//         { title: t("edit"), path: paths.dashboard.product.demo.edit },
//       ],
//     },
//   ],
// },

// MANAGEMENT
// ----------------------------------------------------------------------
// {
//   subheader: t("management"),
//   items: [
//     // USER
//     {
//       title: t("user"),
//       path: paths.dashboard.user.root,
//       icon: ICONS.user,
//       children: [
//         { title: t("profile"), path: paths.dashboard.user.root },
//         { title: t("cards"), path: paths.dashboard.user.cards },
//         { title: t("list"), path: paths.dashboard.user.list },
//         { title: t("create"), path: paths.dashboard.user.new },
//         { title: t("edit"), path: paths.dashboard.user.demo.edit },
//         { title: t("account"), path: paths.dashboard.user.account },
//       ],
//     },

//     // PRODUCT
// {
//   title: t("product"),
//   path: paths.dashboard.product.root,
//   icon: ICONS.product,
//   children: [
//     { title: t("list"), path: paths.dashboard.product.root },
//     {
//       title: t("details"),
//       path: paths.dashboard.product.demo.details,
//     },
//     { title: t("create"), path: paths.dashboard.product.new },
//     { title: t("edit"), path: paths.dashboard.product.demo.edit },
//   ],
// },

//     // ORDER
//     {
//       title: t("order"),
//       path: paths.dashboard.order.root,
//       icon: ICONS.order,
//       children: [
//         { title: t("list"), path: paths.dashboard.order.root },
//         { title: t("details"), path: paths.dashboard.order.demo.details },
//       ],
//     },

//     // INVOICE
//     {
//       title: t("invoice"),
//       path: paths.dashboard.invoice.root,
//       icon: ICONS.invoice,
//       children: [
//         { title: t("list"), path: paths.dashboard.invoice.root },
//         {
//           title: t("details"),
//           path: paths.dashboard.invoice.demo.details,
//         },
//         { title: t("create"), path: paths.dashboard.invoice.new },
//         { title: t("edit"), path: paths.dashboard.invoice.demo.edit },
//       ],
//     },

//     // BLOG
//     {
//       title: t("blog"),
//       path: paths.dashboard.post.root,
//       icon: ICONS.blog,
//       children: [
//         { title: t("list"), path: paths.dashboard.post.root },
//         { title: t("details"), path: paths.dashboard.post.demo.details },
//         { title: t("create"), path: paths.dashboard.post.new },
//         { title: t("edit"), path: paths.dashboard.post.demo.edit },
//       ],
//     },

//     // JOB
//     {
//       title: t("job"),
//       path: paths.dashboard.job.root,
//       icon: ICONS.job,
//       children: [
//         { title: t("list"), path: paths.dashboard.job.root },
//         { title: t("details"), path: paths.dashboard.job.demo.details },
//         { title: t("create"), path: paths.dashboard.job.new },
//         { title: t("edit"), path: paths.dashboard.job.demo.edit },
//       ],
//     },

//     // TOUR
//     {
//       title: t("tour"),
//       path: paths.dashboard.tour.root,
//       icon: ICONS.tour,
//       children: [
//         { title: t("list"), path: paths.dashboard.tour.root },
//         { title: t("details"), path: paths.dashboard.tour.demo.details },
//         { title: t("create"), path: paths.dashboard.tour.new },
//         { title: t("edit"), path: paths.dashboard.tour.demo.edit },
//       ],
//     },

//     // FILE MANAGER
//     {
//       title: t("file_manager"),
//       path: paths.dashboard.fileManager,
//       icon: ICONS.folder,
//     },

//     // MAIL
//     {
//       title: t("mail"),
//       path: paths.dashboard.mail,
//       icon: ICONS.mail,
//       info: <Label color="error">+32</Label>,
//     },

//     // CHAT
//     // {
//     //   title: t('chat'),
//     //   path: paths.dashboard.chat,
//     //   icon: ICONS.chat,
//     // },

//     // CALENDAR
//     {
//       title: t("calendar"),
//       path: paths.dashboard.calendar,
//       icon: ICONS.calendar,
//     },

//     // KANBAN
//     {
//       title: t("kanban"),
//       path: paths.dashboard.kanban,
//       icon: ICONS.kanban,
//     },
//   ],
// },

// // DEMO MENU STATES
// {
//   subheader: t(t("other_cases")),
//   items: [
//     {
//       // default roles : All roles can see this entry.
//       // roles: ['user'] Only users can see this item.
//       // roles: ['admin'] Only admin can see this item.
//       // roles: ['admin', 'manager'] Only admin/manager can see this item.
//       // Reference from 'src/guards/RoleBasedGuard'.
//       title: t("item_by_roles"),
//       path: paths.dashboard.permission,
//       icon: ICONS.lock,
//       roles: ["admin", "manager"],
//       caption: t("only_admin_can_see_this_item"),
//     },
//     {
//       title: t("menu_level"),
//       path: "#/dashboard/menu_level",
//       icon: ICONS.menuItem,
//       children: [
//         {
//           title: t("menu_level_1a"),
//           path: "#/dashboard/menu_level/menu_level_1a",
//         },
//         {
//           title: t("menu_level_1b"),
//           path: "#/dashboard/menu_level/menu_level_1b",
//           children: [
//             {
//               title: t("menu_level_2a"),
//               path: "#/dashboard/menu_level/menu_level_1b/menu_level_2a",
//             },
//             {
//               title: t("menu_level_2b"),
//               path: "#/dashboard/menu_level/menu_level_1b/menu_level_2b",
//               children: [
//                 {
//                   title: t("menu_level_3a"),
//                   path: "#/dashboard/menu_level/menu_level_1b/menu_level_2b/menu_level_3a",
//                 },
//                 {
//                   title: t("menu_level_3b"),
//                   path: "#/dashboard/menu_level/menu_level_1b/menu_level_2b/menu_level_3b",
//                 },
//               ],
//             },
//           ],
//         },
//       ],
//     },
//     {
//       title: t("item_disabled"),
//       path: "#disabled",
//       icon: ICONS.disabled,
//       disabled: true,
//     },
//     {
//       title: t("item_label"),
//       path: "#label",
//       icon: ICONS.label,
//       info: (
//         <Label
//           color="info"
//           startIcon={<Iconify icon="solar:bell-bing-bold-duotone" />}
//         >
//           NEW
//         </Label>
//       ),
//     },
//     {
//       title: t("item_caption"),
//       path: "#caption",
//       icon: ICONS.menuItem,
//       caption:
//         "Quisque malesuada placerat nisl. In hac habitasse platea dictumst. Cras id dui. Pellentesque commodo eros a enim. Morbi mollis tellus ac sapien.",
//     },
//     {
//       title: t("item_external_link"),
//       path: "https://www.google.com/",
//       icon: ICONS.external,
//     },
//     {
//       title: t("blank"),
//       path: paths.dashboard.blank,
//       icon: ICONS.blank,
//     },
//   ],
// },
import { usePlayerCapabilities } from "src/api/federation-player";
